import Collection from './collection.js';
import PayrollDetail from 'newModels/payroll-detail.js';
import PayrollItemFilters from './mixins/payroll-item-filters.js';
import axios from 'axios';
import Decimal from 'decimal.js';

export default class PayrollDetails extends PayrollItemFilters(Collection) {
    url() {
        if (this.payroll && this.payroll.id) {
            return `${this.payroll.url()}/details`;
        }

        if (this.employee) {
            return `${this.employee.url()}/payrollDetails`;
        }

        return `/PayrollDetails`;
    }

    initialize(models, {
        payroll = null,
        employee = null,
        autoCalcZakat = true,
        pagination = false
    } = {}) {
        // Make sure pagination is disabled for this collection
        super.initialize(
            models,
            Object.assign(arguments[1] || {}, {pagination})
        );

        this.model = PayrollDetail;
        this.payroll = payroll;
        this.employee = employee;
        this.autoCalcZakat = autoCalcZakat;

        if (this.autoCalcZakat === true) {
            this.on('reset', this.updateZakats, this);
            this.on('add remove change:amount destroy', this.updateZakat, this);
            setTimeout(this.updateZakats.bind(this), 1);
        }

        if (this.payroll) {
            this.payroll.on('sync', this.updatePayrollId, this);
        }

        return this;
    }

    comparator(a, b) {
        // jscs:disable requireCamelCaseOrUpperCaseIdentifiers
        const weightByType = {
            base:         100,
            addition:     90,
            benefit:      80,
            deduction:    70,
            unpaid_leave: 65,
            cp38:         63,
            mtd:          60,
            epf:          50,
            socso:        40,
            zakat:        30
        };
        // jscs:enable requireCamelCaseOrUpperCaseIdentifiers
        const itemA = a.getPayrollItem();
        const itemB = b.getPayrollItem();

        if (a.get('isAdditional') > b.get('isAdditional')) {
            return 1;
        }

        if (!itemA || !itemB) {
            return 1;
        }

        if (
            weightByType[itemA.get('type')] <
            weightByType[itemB.get('type')]
        ) {
            return 1;
        }
    }

    setPayroll(payroll) {
        this.payroll = payroll;
        this.updatePayrollId();
    }

    updatePayrollId() {
        this.each(model => model.set({payrollId: this.payroll.id}, {
            silent: true,
            validate: false
        }));
    }

    updateZakats() {
        const employeeIds = _.uniq(this.map(model => {
            return model.get('employeeId');
        }));
        employeeIds.forEach(id => {
            this.filterByEmployee(id).forEach(this.updateZakat.bind(this));
        });
    }

    updateZakat(model) {
        if (model.isZakat()) {
            return;
        }

        const net = this.getNetAmount(model.get('employeeId'));
        const zakats = this.filter(detail => {
            return (
                !detail.isAdditional() &&
                detail.isZakat() &&
                detail.get('rate') &&
                model.get('employeeId') === detail.get('employeeId')
            );
        });

        zakats.forEach(zakat => {
            zakat.set('amount', net * (zakat.get('rate') / 100));
        });
    }

    replace(details, employeeId) {
        const models = employeeId ?
            this.filterByEmployee(employeeId) : this.models;

        return Promise.all(models.map(detail => {
            // clear all additional items
            if (detail.get('isAdditional')) {
                this.remove(detail, {validate: false});
                if (detail.id) {
                    return detail.destroy();
                }
            }
            return Promise.resolve();
        })).then(() => {
            details.each(prevDetail => {
                const attrs = (prevDetail && _.isObject(prevDetail.attributes)) ?
                    prevDetail.attributes : prevDetail;
                delete attrs.objectId;
                delete attrs.createdAt;
                delete attrs.updatedAt;
                const currDetail = this.find(detail => (
                    detail.get('payrollItemId') === attrs.payrollItemId &&
                        detail.get('employeeId') === attrs.employeeId
                ));

                // add any new additional items
                if (!currDetail && attrs.isAdditional === true) {
                    return this.add(attrs, {validate: false});
                }

                if (currDetail) {
                    currDetail.set({unit: attrs.unit});

                    if (!currDetail.isLocked()) {
                        currDetail.set({
                            rate:   attrs.rate,
                            amount: attrs.amount
                        });
                    }
                }
            });
        });
    }

    reassignToPayroll(payrollId) {
        this.forEach(detail => detail.set({payrollId}));
        return this;
    }

    /**
     * Get the sum of all items that affect the net amount
     *
     * @instance
     * @returns {Number}
     */
    getNetAmount(employeeId) {
        const models = employeeId ?
            this.filterByEmployee(employeeId) :
            this.models;

        return models.reduce((memo, detail) => {
            const payrollItem = detail.getPayrollItem();
            if (payrollItem && payrollItem.get('isNet')) {
                return memo.plus(detail.getRealAmount() || 0);
            }
            return memo;
        }, new Decimal(0)).toDP(2, Decimal.ROUND_HALF_UP).toNumber();
    }

    /**
     * Create a new collection with all payslip details that are affected by
     * unpaid leave
     *
     * @instance
     * @returns {module:collections.PayslipDetails}
     */
    findAffectedByUnpaidLeave() {
        return new PayrollDetails(this.filter(model => {
            return model.get('isUnpaidLeave') === true;
        }));
    }

    filterByEmployee(employeeId, {collection = false} = {}) {
        const models = this.filter(model => {
            return model.get('employeeId') === employeeId;
        });

        models.sort(function(x, y) {
            return x.getPayrollItem().get('name') === 'Salary (Basic)' ? -1 :
                y.getPayrollItem().get('name') === 'Salary (Basic)' ? 1 : 0;
        });

        if (collection === true) {
            return new PayrollDetails(models, {payroll: this.payroll});
        }
        return models;
    }

    filterNonContributions() {
        const models = this.filter(model => {
            const item = model.getPayrollItem();
            const type = item.get('type');
            const subtype = item.get('subtype');

            return (
                item && (
                    ['mtd', 'epf', 'socso', 'eis', 'hrdf'].indexOf(type) < 0 ||
                    (type === 'mtd' && subtype === 'custom')
                )
            );
        });

        return new PayrollDetails(models, {payroll: this.payroll});
    }

    static createDefaults(payroll, employees) {
        if (employees.length === 0) {
            return Promise.resolve();
        }
        const url = new PayrollDetails(null, {}).finalUrl();
        const employeeIds = employees.map(employee => employee.id);
        return axios.post(
            `${url}/createDefaults`,
            {
                companyId: app.company.id,
                employeeIds,
                period: payroll.get('periodDate') || undefined
            },
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(r => {
            const details = new PayrollDetails(
                r.data.map(detail => {
                    if (payroll.id) {
                        // this isn't a backbone model yet
                        detail.payrollId = payroll.id;
                    }
                    return detail;
                }),
                {payroll}
            );

            return Promise.all(details.map(detail => {
                if (detail.isOvertime()) {
                    const employee = employees.find(model => {
                        return model.id === detail.get('employeeId');
                    });
                    return detail.createOvertimeInfo(payroll, employee);
                }
                return detail;
            })).then(() => details);
        })
            .catch(err => {
                if (err.response) {
                    throw err.response.data.error;
                }
                throw err;
            });
    }

    createOvertimeInfoAfterAdd(employee) {
        return Promise.all(this.map(detail => {
            if (detail.isOvertime()) {
                const currentEmployee =
                    employee && detail.get('employeeId') === employee.id ?
                        employee : null;
                if (currentEmployee) {
                    return detail.createOvertimeInfo(
                        this.payroll, currentEmployee
                    );
                }
                return detail;
            }
            return detail;
        }));
    }
}



// WEBPACK FOOTER //
// ./app/new-collections/payroll-details.js